<template>
	<div class="resizable-splitter-container" ref="container">
		<div class="splitter-pane top-pane"
			:style="{ height: enableDrag ? `calc(${topPercentage}% - var(--splitter-height))` : `${topPercentage}%` }">
			<slot name="top"></slot>
		</div>
		<div v-if="enableDrag" class="splitter-divider" @mousedown="startDrag">
			<div class="splitter-handle">
				<div class="splitter-handle-bar"></div>
			</div>
		</div>
		<div v-if="enableDrag" class="splitter-pane bottom-pane">
			<slot name="bottom"></slot>
		</div>
	</div>
</template>

<script>
export default {
	name: "ResizableSplitter",
	props: {
		initialTopPercentage: {
			type: Number,
			default: 50,
		},
		minTopPercentage: {
			type: Number,
			default: 0,
		},
		maxTopPercentage: {
			type: Number,
			default: 100,
		},
		enableDrag: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			topPercentage: 50,
			isDragging: false,
			startY: 0,
			startPercentage: 50,
		};
	},
	watch: {
		topPercentage(newVal) {
			this.$emit("resize");
		},
		// Apply when initialTopPercentage changes (for page transitions)
		initialTopPercentage(newVal) {
			this.updateTopPercentage(newVal);
		},
	},
	methods: {
		updateTopPercentage(value) {
			if (value === this.topPercentage) {
				return;
			}

			const effectiveMin = Math.max(this.minTopPercentage, 0);
			const effectiveMax = Math.min(
				100,
				Math.max(effectiveMin, this.maxTopPercentage),
			);

			this.topPercentage = Math.max(
				effectiveMin,
				Math.min(effectiveMax, value),
			);
		},
		startDrag(event) {
			if (!this.enableDrag) {
				return;
			}
			event.preventDefault();

			this.isDragging = true;
			this.startY = event.clientY;
			this.startPercentage = this.topPercentage;

			document.addEventListener("mousemove", this.onDrag);
			document.addEventListener("mouseup", this.stopDrag);
			document.body.style.userSelect = "none";
			document.body.style.cursor = "ns-resize";
		},
		onDrag(event) {
			if (!this.isDragging) return;

			const container = this.$refs.container;
			if (!container) return;

			const rect = container.getBoundingClientRect();
			const currentY = event.clientY;
			const deltaY = currentY - this.startY;
			const deltaPercentage = (deltaY / rect.height) * 100;

			let newPercentage = this.startPercentage + deltaPercentage;
			const effectiveMin = Math.max(this.minTopPercentage, 0);
			const effectiveMax = Math.min(
				100,
				Math.max(effectiveMin, this.maxTopPercentage),
			);
			newPercentage = Math.max(
				effectiveMin,
				Math.min(effectiveMax, newPercentage),
			);
			this.topPercentage = newPercentage;

			event.preventDefault();
		},
		stopDrag() {
			if (!this.isDragging) return;

			this.isDragging = false;
			document.removeEventListener("mousemove", this.onDrag);
			document.removeEventListener("mouseup", this.stopDrag);
			document.body.style.userSelect = "";
			document.body.style.cursor = "";

			this.$emit("resize-end");
		},
		handleWindowResize() {
			this.$emit("window-resize");
		},
	},
	mounted() {
		this.updateTopPercentage(this.initialTopPercentage);
		window.addEventListener("resize", this.handleWindowResize);
	},
	beforeUnmount() {
		if (this.isDragging) {
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
		}
		window.removeEventListener("resize", this.handleWindowResize);
		document.removeEventListener("mousemove", this.onDrag);
		document.removeEventListener("mouseup", this.stopDrag);
	},
};
</script>

<style scoped>
.resizable-splitter-container {
	--splitter-height: 6px;
	width: 100%;
	height: 100%;
	/* Prevent exceeding viewport height, considering header area */
	max-height: calc(100vh - 48px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.splitter-pane {
	position: relative;
	overflow: hidden;
}

.top-pane {
	flex-shrink: 0;
}

.bottom-pane {
	flex: 1;
}

.splitter-divider {
	height: var(--splitter-height);
	flex-shrink: 0;
	background-color: #2d2d30;
	position: relative;
	z-index: 100;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background-color 0.2s;
}

.splitter-divider {
	cursor: ns-resize;
}

.splitter-divider:hover {
	background-color: #0064ba;
}

.splitter-divider:active {
	background-color: #0078d4;
}

.splitter-handle {
	width: 60px;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.splitter-handle-bar {
	width: 40px;
	height: 3px;
	background-color: rgba(255, 255, 255, 0.5);
	border-radius: 2px;
	transition: background-color 0.2s;
}

.splitter-divider:hover .splitter-handle-bar {
	background-color: rgba(255, 255, 255, 0.9);
}
</style>
